import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Clock } from 'lucide-react';

import PublicNavBar from '@/components/landing/PublicNavBar';
import { useSeoMeta } from '@/lib/useSeoMeta';
import { useHreflang } from '@/lib/useHreflang';
import StructuredData, { breadcrumbLd, blogIndexLd } from '@/components/seo/StructuredData';

const API = process.env.REACT_APP_BACKEND_URL;

const NON_EN_LOCALES = ['fr', 'de', 'es', 'pt', 'zh'];

// Language tab pills — each links to the locale-specific blog URL.
// Clicking a tab navigates; the active tab is derived from the current
// i18n language, so /fr/blog highlights "Français" automatically.
const LANGUAGE_TABS = [
  { code: 'en', label: 'English',   path: '/blog' },
  { code: 'fr', label: 'Français',  path: '/fr/blog' },
  { code: 'de', label: 'Deutsch',   path: '/de/blog' },
  { code: 'es', label: 'Español',   path: '/es/blog' },
  { code: 'pt', label: 'Português', path: '/pt/blog' },
  { code: 'zh', label: '中文',       path: '/zh/blog' },
];

/* 2026-10 — Public blog index. Static, crawlable, no auth-wall.
 * 2026-02 — Added i18n chrome (UI labels in 6 languages) and full
 * hreflang coverage for all locale-prefixed routes (/fr/blog, etc.).
 */
export default function BlogIndexPage() {
  const { t, i18n } = useTranslation();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Locale-aware links — must be defined before useEffect so it can depend on lang
  const lang = (i18n.resolvedLanguage || i18n.language || 'en').slice(0, 2);
  const isLocale = NON_EN_LOCALES.includes(lang);
  const landingPath = isLocale ? `/${lang}` : '/';
  const bookstorePath = isLocale ? `/${lang}/bookstore` : '/bookstore';
  const postPath = (slug) => isLocale ? `/${lang}/blog/${slug}` : `/blog/${slug}`;

  useSeoMeta('/blog', {
    title: 'The Loom Blog — AI-Assisted Novel Writing for Indie Authors | Crown & Rose',
    description: 'Deep, practical writing on AI-assisted novel writing for indie authors: mixed-LLM continuity, the craft multiplier, publishing and royalties, and how The Loom keeps long manuscripts from stalling.',
    keywords: 'AI assisted novel writing, indie author tools, mixed LLM writing, craft multiplier, novel writing blog',
  });
  useHreflang('blog');

  const siteUrl = typeof window !== 'undefined' ? window.location.origin : 'https://crownnrose.com';

  useEffect(() => {
    // Each language tab shows only its own locale's posts.
    // English uses ?locale=en so it shows only English posts (not all posts).
    const params = `?locale=${lang}`;
    axios.get(`${API}/api/blog${params}`).then((r) => {
      setPosts(r.data?.posts || []);
    }).catch(() => setPosts([])).finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  return (
    <div className="h-full overflow-y-auto bg-background text-foreground" data-testid="blog-index-page">
      <StructuredData id="blog-breadcrumb" data={breadcrumbLd([
        { name: 'Home', item: `${siteUrl}/` },
        { name: 'Blog', item: `${siteUrl}/blog` },
      ])} />
      <StructuredData id="blog-index" data={blogIndexLd({ siteUrl, lang })} />

      <PublicNavBar
        onSignIn={() => { window.location.href = '/login'; }}
        onSignUp={() => { window.location.href = '/signup'; }}
      />

      <main className="mx-auto max-w-4xl px-4 py-10 sm:px-6">
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight" data-testid="blog-index-title">
          {t('blog.pageTitle')}
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground">
          {t('blog.subtitle')}
        </p>

        {/* Language selector tabs */}
        <nav
          aria-label="Blog language selector"
          data-testid="blog-language-tabs"
          className="mt-6 flex flex-wrap items-center gap-2"
        >
          {LANGUAGE_TABS.map(({ code, label, path }) => (
            <Link
              key={code}
              to={path}
              data-testid={`blog-lang-tab-${code}`}
              aria-current={lang === code ? 'page' : undefined}
              className={[
                'rounded-full border px-4 py-1.5 text-[13px] font-medium transition-colors no-underline',
                lang === code
                  ? 'border-rose-300 bg-rose-50 text-rose-800 dark:border-rose-700 dark:bg-rose-950 dark:text-rose-300'
                  : 'border-border bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground',
              ].join(' ')}
            >
              {label}
            </Link>
          ))}
        </nav>

        {loading && <div className="mt-10 text-sm text-muted-foreground" data-testid="blog-loading">{t('blog.loading')}</div>}
        {!loading && posts.length === 0 && (
          <div className="mt-10 text-sm text-muted-foreground" data-testid="blog-empty-state">
            {t('blog.empty')}
          </div>
        )}

        <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2" data-testid="blog-posts-grid">
          {posts.map((p) => (
            <Link
              key={p.id}
              to={postPath(p.slug)}
              className="group rounded-lg border border-border bg-card p-5 transition hover:border-primary"
              data-testid={`blog-post-card-${p.slug}`}
            >
              {p.cover_image_url && (
                <img
                  src={p.cover_image_url.startsWith('/') ? `${API}${p.cover_image_url}` : p.cover_image_url}
                  alt=""
                  className="mb-3 h-36 w-full rounded-md object-cover"
                />
              )}
              <h2 className="text-lg font-semibold leading-snug group-hover:text-primary">{p.title}</h2>
              {p.dek && <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{p.dek}</p>}
              <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  <Clock className="h-3 w-3" /> {p.reading_minutes} {t('blog.minRead')}
                </span>
                {(p.tags || []).map((tag) => (
                  <span key={tag} className="rounded-full bg-muted px-2 py-0.5">{tag}</span>
                ))}
              </div>
            </Link>
          ))}
        </div>
      </main>
    </div>
  );
}
